/**
 * MutationQueue — sits between the SW (or client) and a QueueStore.
 *
 * Responsibilities:
 *   • enqueue: persist a QueuedMutation, returning the assigned id
 *   • pending / pendingCount: read state for the UI
 *   • replay: walk the queue oldest-first, attempt each, remove on
 *     success, increment attempts + lastError on failure, evict after
 *     maxAttempts
 *
 * The replay loop does NOT mutate the headers it reads from the store
 * directly — it builds a fresh init object so the Authorization header
 * is always current at the moment of the replay (not whenever the row
 * was enqueued, which could be hours ago and well past JWT expiry).
 */

import type {
  QueuedMutation,
  QueueStore,
  ReplayHooks,
  ReplayResult,
} from './types'

const DEFAULT_MAX_ATTEMPTS = 5

/**
 * Generate a stable id. Falls back to a Math.random() form when
 * crypto.randomUUID is unavailable (older test envs); the format is
 * still URL-safe and unique enough for queue purposes.
 */
function makeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export interface EnqueueInput {
  url:            string
  method:         QueuedMutation['method']
  headers:        Record<string, string>
  body:           string
  idempotencyKey: string
}

export class MutationQueue {
  constructor(private readonly store: QueueStore) {}

  async enqueue(input: EnqueueInput): Promise<QueuedMutation> {
    const m: QueuedMutation = {
      id:             makeId(),
      url:            input.url,
      method:         input.method,
      headers:        sanitiseHeaders(input.headers),
      body:           input.body,
      idempotencyKey: input.idempotencyKey,
      queuedAt:       Date.now(),
      attempts:       0,
    }
    await this.store.put(m)
    return m
  }

  async pending(): Promise<readonly QueuedMutation[]> {
    return this.store.list()
  }

  async pendingCount(): Promise<number> {
    return this.store.count()
  }

  async clear(): Promise<void> {
    await this.store.clear()
  }

  /**
   * Walks the queue, replaying each mutation. On success removes the row;
   * on failure increments attempts and re-stores with `lastError`. After
   * maxAttempts the row is evicted to keep the queue from growing without
   * bound — the audit log on the server side will already record any
   * partial application.
   */
  async replay(hooks: ReplayHooks): Promise<ReplayResult> {
    const max     = hooks.maxAttempts ?? DEFAULT_MAX_ATTEMPTS
    const items   = await this.store.list()
    let replayed  = 0
    let failed    = 0

    for (const item of items) {
      const headers: Record<string, string> = { ...item.headers }
      headers['Idempotency-Key'] = item.idempotencyKey

      if (hooks.freshAuthToken) {
        const tok = await hooks.freshAuthToken()
        if (tok) headers['Authorization'] = `Bearer ${tok}`
      }

      let res: Response | null = null
      let err: unknown          = null
      try {
        res = await hooks.fetch(item.url, {
          method:  item.method,
          headers,
          body:    item.body.length > 0 ? item.body : undefined,
        })
      } catch (e) {
        err = e
      }

      // Treat network errors AND 5xx as retryable. 4xx is a permanent
      // outcome — the server rejected the request on its merits, so
      // remove the row and count it as replayed (the SUBMIT happened,
      // it just got an error response that the user already saw).
      const ok          = !!res && res.ok
      const retryable   = !res || (res.status >= 500 && res.status < 600)

      if (ok || (!retryable && res !== null)) {
        await this.store.remove(item.id)
        replayed++
        continue
      }

      const newAttempts = item.attempts + 1
      const errMsg      = err instanceof Error
                            ? err.message.slice(0, 200)
                            : (res ? `HTTP ${res.status}` : 'unknown error')

      if (newAttempts >= max) {
        await this.store.remove(item.id)
        failed++
        continue
      }

      await this.store.put({
        ...item,
        attempts:  newAttempts,
        lastError: errMsg,
      })
      failed++
    }

    return { attempted: items.length, replayed, failed }
  }
}

/**
 * Strip headers that must never be persisted across an offline window.
 * Authorization is reconstructed at replay time from a fresh JWT.
 */
function sanitiseHeaders(headers: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(headers)) {
    const lower = k.toLowerCase()
    if (lower === 'authorization') continue
    if (lower === 'cookie')        continue
    out[k] = v
  }
  return out
}
